var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PaletteIcon from '@mui/icons-material/Palette';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import LoadingButton from '@mui/lab/LoadingButton';
import { loadPairedScreens, pairScreen, revokeScreen, setScreenTheme, } from '../../api/screenPairing';
import { ModalNew } from '../../components/atoms/Modal/Modal';
import { colors } from '../../styles/colors';
import { formatScreenCode, isValidScreenCode, normalizeScreenCode, SCREEN_CODE_LENGTH, } from '../../utils';
import { DEFAULT_SCREEN_THEME, getScreenThemeLabel, SCREEN_THEMES, } from './themes';
import ScreenQrScanner from './ScreenQrScanner';
const Shell = styled.div `
  width: min(560px, 92vw);
  max-height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  background: ${colors.surface};
  color: ${colors.textPrimary};
`;
const Header = styled.div `
  padding: 20px 24px 0;
`;
const Body = styled.div `
  padding: 20px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
`;
const ThemeGrid = styled.div `
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;
const ThemeCard = styled.button `
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px;
  border-radius: 12px;
  background: ${colors.surfaceVariant};
  border: 2px solid ${({ selected }) => (selected ? colors.primary : colors.cardBorder)};
  color: ${colors.textPrimary};
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s;

  &:hover {
    border-color: ${colors.primary};
  }
`;
const Swatches = styled.div `
  display: flex;
  gap: 6px;
`;
const Swatch = styled.span `
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: ${({ color }) => color};
  border: 1px solid rgba(255, 255, 255, 0.1);
`;
const ScreenRow = styled.div `
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  border-radius: 10px;
  border: 1px solid ${colors.cardBorder};
  background: ${colors.surfaceVariant};
`;
const RowMain = styled.div `
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;
const CodeChip = styled.span `
  display: inline-block;
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: 0.08em;
  padding: 2px 8px;
  border-radius: 6px;
  background: ${colors.secondaryDark};
  color: ${colors.textPrimary};
`;
const RowActions = styled.div `
  display: flex;
  gap: 8px;
  flex-wrap: wrap;

  // Планшет: тач-таргеты >= 44px, чтобы ведущий попадал пальцем.
  & > button {
    min-height: 44px;
  }
`;
const SmallMuted = styled.span `
  font-size: 12px;
  color: ${colors.textSecondary};
`;
const formatLastSeen = (lastSeenAt) => {
    if (!lastSeenAt)
        return '—';
    const diffSec = Math.max(0, Math.floor(Date.now() / 1000 - lastSeenAt));
    if (diffSec < 60)
        return 'на связи только что';
    if (diffSec < 60 * 60) {
        const m = Math.floor(diffSec / 60);
        return `на связи ${m} мин назад`;
    }
    const date = new Date(lastSeenAt * 1000);
    const hh = date.getHours().toString().padStart(2, '0');
    const mm = date.getMinutes().toString().padStart(2, '0');
    return `на связи ${hh}:${mm}`;
};
const extractErrorMessage = (err, fallback) => {
    var _a, _b;
    if (!axios.isAxiosError(err))
        return fallback;
    const status = (_a = err.response) === null || _a === void 0 ? void 0 : _a.status;
    if (status === 404)
        return 'Код не найден или устарел';
    if (status === 403)
        return 'Этот экран привязан к другому ведущему';
    const data = (_b = err.response) === null || _b === void 0 ? void 0 : _b.data;
    if (data === null || data === void 0 ? void 0 : data.detail)
        return data.detail;
    if (data === null || data === void 0 ? void 0 : data.title)
        return data.title;
    if (err.message === 'Network Error')
        return 'Нет связи с сервером';
    return fallback;
};
export const ScreenPairDialog = ({ open, onClose, matchId, }) => {
    var _a;
    const [tab, setTab] = useState('pair');
    const [step, setStep] = useState('input');
    const [mode, setMode] = useState('scan');
    const [codeInput, setCodeInput] = useState('');
    const [codeError, setCodeError] = useState(null);
    const [verifiedCode, setVerifiedCode] = useState('');
    const [selectedTheme, setSelectedTheme] = useState(DEFAULT_SCREEN_THEME);
    const [isPairing, setIsPairing] = useState(false);
    const [pairedScreen, setPairedScreen] = useState(null);
    const [screens, setScreens] = useState([]);
    const [isLoadingList, setIsLoadingList] = useState(false);
    const [listError, setListError] = useState(null);
    const [pendingThemeChangeId, setPendingThemeChangeId] = useState(null);
    const [pendingRevokeId, setPendingRevokeId] = useState(null);
    const [listActionError, setListActionError] = useState(null);
    const refreshTimer = useRef(null);
    // Меню смены темы и диалог подтверждения отключения — вместо «слепого» toggle
    // по иконке. На планшете промахнуться по палитре/корзине ~14px было легко,
    // поэтому действия подписаны и защищены подтверждением.
    const [themeMenu, setThemeMenu] = useState(null);
    const [confirmRevoke, setConfirmRevoke] = useState(null);
    const resetPairFlow = useCallback(() => {
        setStep('input');
        setMode('scan');
        setCodeInput('');
        setCodeError(null);
        setVerifiedCode('');
        setSelectedTheme(DEFAULT_SCREEN_THEME);
        setIsPairing(false);
        setPairedScreen(null);
    }, []);
    const refreshScreens = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        setIsLoadingList(true);
        setListError(null);
        try {
            const list = yield loadPairedScreens();
            setScreens(list);
        }
        catch (err) {
            setListError(extractErrorMessage(err, 'Не удалось загрузить список экранов'));
        }
        finally {
            setIsLoadingList(false);
        }
    }), []);
    // На закрытие — сбросить все состояния, чтобы диалог открывался чистым.
    useEffect(() => {
        if (!open) {
            resetPairFlow();
            setTab('pair');
            setListError(null);
            setListActionError(null);
            return;
        }
        refreshScreens();
    }, [open, refreshScreens, resetPairFlow]);
    // Обновление списка раз в 30 секунд, пока открыто.
    useEffect(() => {
        if (!open)
            return undefined;
        refreshTimer.current = setInterval(() => {
            refreshScreens();
        }, 30000);
        return () => {
            if (refreshTimer.current) {
                clearInterval(refreshTimer.current);
                refreshTimer.current = null;
            }
        };
    }, [open, refreshScreens]);
    const handleCodeDetected = useCallback((code) => {
        setCodeError(null);
        setVerifiedCode(code);
        setCodeInput(formatScreenCode(code));
        setStep('theme');
    }, []);
    const handleManualSubmit = () => {
        if (!isValidScreenCode(codeInput)) {
            setCodeError('Код состоит из 6 символов. Разрешены буквы и цифры из QR экрана.');
            return;
        }
        setCodeError(null);
        setVerifiedCode(normalizeScreenCode(codeInput));
        setStep('theme');
    };
    const handlePair = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!verifiedCode)
            return;
        setIsPairing(true);
        setCodeError(null);
        try {
            const screen = yield pairScreen({
                code: verifiedCode,
                theme: selectedTheme,
                matchId: matchId !== null && matchId !== void 0 ? matchId : undefined,
            });
            setPairedScreen(screen);
            setStep('success');
            refreshScreens();
        }
        catch (err) {
            setCodeError(extractErrorMessage(err, 'Не удалось подключить экран'));
            setStep('input');
            setMode('manual');
        }
        finally {
            setIsPairing(false);
        }
    });
    const handleSetTheme = (screen, theme) => __awaiter(void 0, void 0, void 0, function* () {
        if (screen.theme === theme)
            return;
        setPendingThemeChangeId(screen.id);
        setListActionError(null);
        try {
            const updated = yield setScreenTheme(screen.id, theme);
            setScreens((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
        }
        catch (err) {
            setListActionError(extractErrorMessage(err, 'Не удалось сменить тему'));
        }
        finally {
            setPendingThemeChangeId(null);
        }
    });
    const handleRevoke = (screen) => __awaiter(void 0, void 0, void 0, function* () {
        setPendingRevokeId(screen.id);
        setListActionError(null);
        try {
            yield revokeScreen(screen.id);
            setScreens((prev) => prev.filter((s) => s.id !== screen.id));
        }
        catch (err) {
            setListActionError(extractErrorMessage(err, 'Не удалось отключить экран'));
        }
        finally {
            setPendingRevokeId(null);
        }
    });
    const connectedCount = screens.length;
    const themeCards = useMemo(() => SCREEN_THEMES.map((theme) => (_jsxs(ThemeCard, Object.assign({ type: "button", selected: selectedTheme === theme.id, onClick: () => setSelectedTheme(theme.id) }, { children: [_jsx(Typography, Object.assign({ sx: { fontWeight: 600 } }, { children: theme.label })), _jsx(SmallMuted, { children: theme.description }), _jsxs(Swatches, { children: [_jsx(Swatch, { color: theme.preview.background }), _jsx(Swatch, { color: theme.preview.accent }), _jsx(Swatch, { color: theme.preview.highlight })] })] }), theme.id))), [selectedTheme]);
    if (!open)
        return null;
    return (_jsx(ModalNew, Object.assign({ close: true, onClose: onClose }, { children: _jsxs(Shell, { children: [_jsxs(Header, { children: [_jsx(Typography, Object.assign({ variant: "h6", sx: { color: colors.textPrimary } }, { children: "\u041F\u043E\u0434\u043A\u043B\u044E\u0447\u0435\u043D\u0438\u0435 \u044D\u043A\u0440\u0430\u043D\u0430" })), _jsxs(Tabs, Object.assign({ value: tab, onChange: (_, v) => setTab(v), textColor: "inherit", sx: {
                                mt: 1,
                                minHeight: 36,
                                '& .MuiTab-root': { minHeight: 36, color: colors.textSecondary },
                                '& .Mui-selected': { color: colors.textPrimary },
                                '& .MuiTabs-indicator': { backgroundColor: colors.primary },
                            } }, { children: [_jsx(Tab, { label: "\u041D\u043E\u0432\u044B\u0439 \u044D\u043A\u0440\u0430\u043D", value: "pair" }), _jsx(Tab, { label: connectedCount > 0 ? `Подключённые (${connectedCount})` : 'Подключённые', value: "list" })] }))] }), _jsxs(Body, { children: [tab === 'pair' && step === 'input' && (_jsx(_Fragment, { children: mode === 'scan' ? (_jsx(ScreenQrScanner, { onCodeDetected: handleCodeDetected, onCancel: () => setMode('manual'), onCameraUnavailable: () => setMode('manual') })) : (_jsxs(_Fragment, { children: [_jsx(Typography, Object.assign({ sx: { color: colors.textSecondary } }, { children: "\u041D\u0430 \u0422\u0412 \u043E\u0442\u043A\u0440\u044B\u0442 \u044D\u043A\u0440\u0430\u043D \u0441 QR \u0438 \u043A\u043E\u0434\u043E\u043C \u0438\u0437 6 \u0441\u0438\u043C\u0432\u043E\u043B\u043E\u0432. \u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u043A\u043E\u0434 \u0438\u043B\u0438 \u043E\u0442\u0441\u043A\u0430\u043D\u0438\u0440\u0443\u0439\u0442\u0435 QR \u043A\u0430\u043C\u0435\u0440\u043E\u0439." })), _jsx(TextField, { autoFocus: true, label: "\u041A\u043E\u0434 \u044D\u043A\u0440\u0430\u043D\u0430", placeholder: "XXX-XXX", value: codeInput, onChange: (e) => {
                                            const next = formatScreenCode(e.target.value);
                                            setCodeInput(next);
                                            if (codeError)
                                                setCodeError(null);
                                        }, inputProps: { maxLength: SCREEN_CODE_LENGTH + 1, style: { textTransform: 'uppercase' } }, error: !!codeError, helperText: codeError !== null && codeError !== void 0 ? codeError : ' ', sx: {
                                            '& .MuiInputLabel-root': { color: colors.textSecondary },
                                            '& .MuiOutlinedInput-root': {
                                                color: colors.textPrimary,
                                                '& fieldset': { borderColor: colors.cardBorder },
                                                '&:hover fieldset': { borderColor: colors.primary },
                                            },
                                        } }), _jsxs(Box, Object.assign({ sx: { display: 'flex', gap: 1, flexWrap: 'wrap' } }, { children: [_jsx(Button, Object.assign({ variant: "contained", onClick: handleManualSubmit, disabled: !isValidScreenCode(codeInput) }, { children: "\u0414\u0430\u043B\u0435\u0435" })), _jsx(Button, Object.assign({ variant: "outlined", startIcon: _jsx(QrCodeScannerIcon, {}), onClick: () => {
                                                    setCodeError(null);
                                                    setMode('scan');
                                                } }, { children: "\u0421\u043A\u0430\u043D\u0438\u0440\u043E\u0432\u0430\u0442\u044C QR" }))] }))] })) })), tab === 'pair' && step === 'theme' && (_jsxs(_Fragment, { children: [_jsxs(Typography, Object.assign({ sx: { color: colors.textSecondary } }, { children: ["\u041A\u043E\u0434: ", _jsx(CodeChip, { children: formatScreenCode(verifiedCode) })] })), _jsx(Typography, { children: "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0442\u0435\u043C\u0443 \u043E\u0444\u043E\u0440\u043C\u043B\u0435\u043D\u0438\u044F" }), _jsx(ThemeGrid, { children: themeCards }), codeError && (_jsx(Typography, Object.assign({ sx: { color: colors.error, fontSize: 14 } }, { children: codeError }))), _jsxs(Box, Object.assign({ sx: { display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' } }, { children: [_jsx(LoadingButton, Object.assign({ variant: "contained", loading: isPairing, onClick: handlePair }, { children: "\u041F\u043E\u0434\u043A\u043B\u044E\u0447\u0438\u0442\u044C" })), _jsx(Button, Object.assign({ variant: "outlined", disabled: isPairing, onClick: () => {
                                                setStep('input');
                                                setMode('manual');
                                            } }, { children: "\u041D\u0430\u0437\u0430\u0434" }))] }))] })), tab === 'pair' && step === 'success' && pairedScreen && (_jsxs(_Fragment, { children: [_jsxs(Box, Object.assign({ sx: { display: 'flex', alignItems: 'center', gap: 1 } }, { children: [_jsx(CheckCircleIcon, { sx: { color: colors.success } }), _jsx(Typography, Object.assign({ sx: { fontWeight: 600 } }, { children: "\u042D\u043A\u0440\u0430\u043D \u043F\u043E\u0434\u043A\u043B\u044E\u0447\u0451\u043D" }))] })), _jsxs(Box, Object.assign({ sx: { display: 'flex', flexDirection: 'column', gap: 0.5 } }, { children: [_jsxs(Typography, { children: ["\u041A\u043E\u0434: ", _jsx(CodeChip, { children: formatScreenCode(pairedScreen.code) })] }), _jsxs(Typography, { children: ["\u0422\u0435\u043C\u0430: ", getScreenThemeLabel(pairedScreen.theme)] }), pairedScreen.eventName && (_jsxs(SmallMuted, { children: ["\u0410\u0444\u0438\u0448\u0430: ", pairedScreen.eventName] }))] })), _jsxs(Box, Object.assign({ sx: { display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 } }, { children: [_jsx(Button, Object.assign({ variant: "contained", onClick: () => {
                                                resetPairFlow();
                                                setTab('list');
                                            } }, { children: "\u041A \u0441\u043F\u0438\u0441\u043A\u0443 \u044D\u043A\u0440\u0430\u043D\u043E\u0432" })), _jsx(Button, Object.assign({ variant: "outlined", onClick: () => {
                                                resetPairFlow();
                                            } }, { children: "\u041F\u043E\u0434\u043A\u043B\u044E\u0447\u0438\u0442\u044C \u0435\u0449\u0451" })), _jsx(Button, Object.assign({ variant: "text", color: "inherit", onClick: onClose }, { children: "\u0417\u0430\u043A\u0440\u044B\u0442\u044C" }))] }))] })), tab === 'list' && (_jsxs(_Fragment, { children: [listError && (_jsx(Typography, Object.assign({ sx: { color: colors.error, fontSize: 14 } }, { children: listError }))), listActionError && (_jsx(Typography, Object.assign({ sx: { color: colors.error, fontSize: 14 } }, { children: listActionError }))), !isLoadingList && screens.length === 0 && !listError && (_jsx(Typography, Object.assign({ sx: { color: colors.textSecondary } }, { children: "\u041F\u043E\u043A\u0430 \u043D\u0435\u0442 \u043F\u043E\u0434\u043A\u043B\u044E\u0447\u0451\u043D\u043D\u044B\u0445 \u044D\u043A\u0440\u0430\u043D\u043E\u0432. \u041E\u0442\u043A\u0440\u043E\u0439\u0442\u0435 \u0432\u043A\u043B\u0430\u0434\u043A\u0443 \u00AB\u041D\u043E\u0432\u044B\u0439 \u044D\u043A\u0440\u0430\u043D\u00BB, \u0447\u0442\u043E\u0431\u044B \u043F\u0440\u0438\u0432\u044F\u0437\u0430\u0442\u044C \u0422\u0412 \u043F\u043E QR \u0438\u043B\u0438 \u043A\u043E\u0434\u0443." }))), screens.map((screen) => {
                                    const themeChanging = pendingThemeChangeId === screen.id;
                                    const revoking = pendingRevokeId === screen.id;
                                    return (_jsxs(ScreenRow, { children: [_jsxs(RowMain, { children: [_jsxs(Box, Object.assign({ sx: { display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' } }, { children: [_jsx(CodeChip, { children: formatScreenCode(screen.code) }), _jsx(SmallMuted, { children: getScreenThemeLabel(screen.theme) })] })), _jsxs(SmallMuted, { children: [formatLastSeen(screen.lastSeenAt), screen.eventName ? ` · ${screen.eventName}` : ''] })] }), _jsxs(RowActions, { children: [_jsx(LoadingButton, Object.assign({ variant: "outlined", startIcon: _jsx(PaletteIcon, {}), loading: themeChanging, disabled: revoking, onClick: (e) => setThemeMenu({ anchor: e.currentTarget, screen }), sx: { color: colors.textPrimary, borderColor: colors.cardBorder } }, { children: "\u0422\u0435\u043C\u0430" })), _jsx(LoadingButton, Object.assign({ variant: "outlined", color: "error", startIcon: _jsx(DeleteOutlineIcon, {}), loading: revoking, disabled: themeChanging, onClick: () => setConfirmRevoke(screen) }, { children: "\u041E\u0442\u043A\u043B\u044E\u0447\u0438\u0442\u044C" }))] })] }, screen.id));
                                }), _jsx(Menu, Object.assign({ anchorEl: (_a = themeMenu === null || themeMenu === void 0 ? void 0 : themeMenu.anchor) !== null && _a !== void 0 ? _a : null, open: !!themeMenu, onClose: () => setThemeMenu(null) }, { children: SCREEN_THEMES.map((theme) => (_jsxs(MenuItem, Object.assign({ selected: (themeMenu === null || themeMenu === void 0 ? void 0 : themeMenu.screen.theme) === theme.id, disabled: (themeMenu === null || themeMenu === void 0 ? void 0 : themeMenu.screen.theme) === theme.id, onClick: () => {
                                            if (themeMenu) {
                                                handleSetTheme(themeMenu.screen, theme.id);
                                            }
                                            setThemeMenu(null);
                                        }, sx: { minHeight: 44, gap: 1 } }, { children: [_jsxs(Swatches, { children: [_jsx(Swatch, { color: theme.preview.background }), _jsx(Swatch, { color: theme.preview.accent }), _jsx(Swatch, { color: theme.preview.highlight })] }), theme.label] }), theme.id))) })), _jsxs(Dialog, Object.assign({ open: !!confirmRevoke, onClose: () => setConfirmRevoke(null) }, { children: [_jsx(DialogTitle, { children: "\u041E\u0442\u043A\u043B\u044E\u0447\u0438\u0442\u044C \u044D\u043A\u0440\u0430\u043D?" }), _jsx(DialogContent, { children: _jsxs(Typography, { children: ["\u042D\u043A\u0440\u0430\u043D ", confirmRevoke ? formatScreenCode(confirmRevoke.code) : '', " \u043F\u0435\u0440\u0435\u0441\u0442\u0430\u043D\u0435\u0442 \u043F\u043E\u043A\u0430\u0437\u044B\u0432\u0430\u0442\u044C \u043C\u0430\u0442\u0447. \u0422\u0412 \u043F\u043E\u043A\u0430\u0436\u0435\u0442 \u043D\u043E\u0432\u044B\u0439 QR \u0434\u043B\u044F \u043F\u0440\u0438\u0432\u044F\u0437\u043A\u0438."] }) }), _jsxs(DialogActions, { children: [_jsx(Button, Object.assign({ onClick: () => setConfirmRevoke(null), sx: { minHeight: 44 } }, { children: "\u041E\u0442\u043C\u0435\u043D\u0430" })), _jsx(LoadingButton, Object.assign({ color: "error", variant: "contained", loading: !!confirmRevoke && pendingRevokeId === confirmRevoke.id, onClick: () => __awaiter(void 0, void 0, void 0, function* () {
                                                        if (!confirmRevoke)
                                                            return;
                                                        const target = confirmRevoke;
                                                        setConfirmRevoke(null);
                                                        yield handleRevoke(target);
                                                    }), sx: { minHeight: 44 } }, { children: "\u041E\u0442\u043A\u043B\u044E\u0447\u0438\u0442\u044C" }))] })] })), _jsxs(Box, Object.assign({ sx: { display: 'flex', justifyContent: 'space-between' } }, { children: [_jsx(Button, Object.assign({ variant: "text", color: "inherit", onClick: refreshScreens, disabled: isLoadingList }, { children: "\u041E\u0431\u043D\u043E\u0432\u0438\u0442\u044C" })), _jsx(Button, Object.assign({ variant: "text", color: "inherit", onClick: onClose }, { children: "\u0417\u0430\u043A\u0440\u044B\u0442\u044C" }))] }))] }))] })] }) })));
};
export default ScreenPairDialog;
